/**
 * Created by stanleyw on 1/24/2023.
 */
import { Injectable } from '@angular/core';
import { LMContextService } from './lm-context.service';
import { ApplicationTracker, DevTrackingClient, StandaloneTrackingClient, startAutoTracking } from '@yggdrasil/core';
import { Attributes, TrackingClient } from '@yggdrasil/core/lib/tracking-client/core';
import { ViewTracker } from '@yggdrasil/core/lib/trackers';
import { Router } from '@angular/router';
import { LMGlobalUtilService } from '@app/utils';
import { LMGlobalTrackerService } from '@utils/lm-global-tracker.service';
import { environment } from '@environments/environment';
import { LMLoggerService } from '@services/lm-logger.service';

@Injectable()
export class LMComponentTrackerService {

  private trackingClient: TrackingClient;
  private viewTracker: ViewTracker;
  private viewExternalTracker: ViewTracker;

  constructor(
    private lmContextService: LMContextService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmGlobalTrackerService: LMGlobalTrackerService,
    private lmLoggerService: LMLoggerService,
    private   router: Router,
  ) {
  }

  //this.lmContextService.getTrackerAccess()

  public enableAutomatictracking() {
    const trackingClient = this.getTrackingClient();
    startAutoTracking({
      trackingClient: trackingClient,
      application: {
        name: "My Application Name",
        identifier: "my.application.id",
      },
      pages: {
        track: {
          type: "path",               // or "hash", "title", "companyon" etc
          ignore: ["ignored/page"],   // Optional
        },
      },
      clicks: {
        track: {
          type: "attributes",
          attributes: ["id", "data-something"],   // Tracks the click of every element with an "id" or "data-something"
          ignore: [/auto-\d*/, "ignored-thing"],  // Optional
          parents: false,                         // Optional
        },
      },
    });
  }

  public enableAngularTracking() {
    const client = this.getTrackingClient();
    const myApplication = this.createApplicationTracker();
  }

  public startExternalComponentTracking(name: string, type: string, info: LMComponentInfo | LMTrackerInfo, loadTime?: number, tags?:  (string)[]) {
    if (!this.lmContextService.isTrackerEnabled()) {
      return;
    }

    this.startExternalViewTracker(name, type, this.buildContentAttributes(info, loadTime), tags);
  }

  public startComponentTracking(info: LMComponentInfo, loadTime?: number, tags?:  (string)[]) {
    if (!this.lmContextService.isTrackerEnabled()) {
      return;
    }

    this.startViewTracker(this.buildContentAttributes(info, loadTime), tags);
  }

  public endComponentTracking() {
    if (!this.lmContextService.isTrackerEnabled()) {
      return;
    }
    this.endViewTracker();
  }

  public endLoadTracking(componentName: string, url:string, info: LMComponentInfo, loadTime: number) {
    if (!this.lmContextService.isTrackerEnabled()) {
      return;
    }
    this.lmGlobalUtilService.updateClientStatsTelegraf(info, loadTime).subscribe((response: any) => {});
    this.serverLogTrackerIssues(componentName, url, info, loadTime);
  }

  private logContentTrackingData(info: LMComponentInfo, loadTime: number) {
    if (!this.lmContextService.isTrackerEnabled()) {
      return;
    }
    //  this.getViewTracker().trackContent("This is the name", "my.content", this.buildContentAttributes(info, loadTime));
  }

  private buildContentAttributes(info: LMComponentInfo | LMTrackerInfo, loadTime: number): Attributes {
    let attrs: Attributes = loadTime ? {"loadTime" : String(loadTime)} : {};
    const keys = Object.keys(info);
    for (let key of keys) {
      if (key === 'pageSpec' || key === 'listSpec' || key === 'formSpec'  || key === 'tenant')  {
        continue;   // skip out we will not use the spec info in the attributes for tracker since it is to many characters in most cases.
      }
      const lowerKey = key.toLowerCase();
      if (lowerKey.indexOf('url') >= 0 ) {
        continue; // skip out and dont send any url's
      }

      const nodeValue = info[key];
      if (nodeValue) {
        if  (typeof nodeValue === 'object') {
          if (!Array.isArray(nodeValue))  { // for now we only handle an object not arrays
            const objKeys = Object.keys(nodeValue);
            for (let objKey of objKeys) {
              const innerNodeValue = nodeValue[objKey];
              if(!innerNodeValue || innerNodeValue?.length <= 0) {
                continue;
              }
              this.processObjectNodesToAttribute(attrs, objKey, innerNodeValue);
            }
          }
        } else if  ((typeof nodeValue === 'string' && nodeValue.length > 0)
          || (typeof nodeValue === 'number')
          || (typeof nodeValue === 'boolean')){
          // seems the backend tracker does not like a value of true or false so they are swapped to yes and no
            this.processObjectNodesToAttribute(attrs, key, nodeValue);
        }
      }
   }
    return attrs;
  }

  private processObjectNodesToAttribute(attrs: Attributes, key: string, nodeValue: number | string | boolean) {
    let pushVal: string = '';
    if (typeof nodeValue === 'boolean') {
      pushVal = nodeValue ? "Yes": "No"
    } else  {
      pushVal = String(nodeValue);
    }

    attrs[this.clipStrLength(key, 256)] = this.clipStrLength(pushVal, 2048);
  }

  private getTrackingClient() {
    return this.trackingClient ? this.trackingClient : this.createTrackingClient()
  }

  private getExternalViewTracker(name?: string, type?: string, attributes?: Attributes, tags?: (string)[]) {
    if (!this.viewExternalTracker) {
      const clippedName = this.clipStrLength(name, 256);
      const userWebRequestId = (attributes && attributes.userWebRequestId ? attributes.userWebRequestId : 'NO WEBREQID') as string;
      tags.push(this.produceTag(userWebRequestId, true));
      this.viewExternalTracker = this.viewExternalTracker ? this.viewExternalTracker : this.landmarkCreateView(clippedName, attributes, tags, type);
    }

    return this.viewExternalTracker;
  }

  private getViewTracker(attributes?: Attributes, tags?: (string)[]) {
    if (!this.viewTracker) {
      const name = this.clipStrLength((attributes && attributes.name ? attributes.name : 'NO NAME') as string, 256);
      const type = (attributes && attributes.type ? attributes.type : 'NO TYPE') as string;
      const componentType = (attributes && attributes.componentType ? attributes.componentType : 'NO COMPTYPE') as string;
      const busClassName = (attributes && attributes.busClassName ? attributes.busClassName : 'NO BUSCLASS') as string;
      const userWebRequestId = (attributes && attributes.userWebRequestId ? attributes.userWebRequestId : 'NO WEBREQID') as string;
      const typeComType = type + '/' + componentType;
      const tags = [this.produceTag(busClassName)  , this.produceTag(componentType), this.produceTag(type)];

      this.viewTracker = this.viewTracker ? this.viewTracker : this.landmarkCreateView(name, attributes, tags, typeComType);
    }

    return this.viewTracker;
  }

  private landmarkCreateView (name: string, attributes?: Attributes, tags?: (string)[], type?: string): ViewTracker {
    return this.getApplicationTracker().createView({
      name: name as string,
      url: this.router.url,
      attributes: attributes ? attributes: {},
      type: this.clipStrLength(type, 256),
      tags: tags
    });
  }

  private clipStrLength(val: string, length: number) {
    return val.substring(0, length-1);
  }

  private produceTag(tag: string, rear?: boolean): string {
    const MAX_LENGTH = 16;
    const len = tag.length;
    let start = 0
    let end = 0;
    if (len > MAX_LENGTH ) {
      if (rear) {
        start = len - MAX_LENGTH;
        end = len;
      }
      else {
        start = 0;
        end = MAX_LENGTH;
      }
      return tag.substring(start, end);
    }

    return tag;
  }

  private getApplicationTracker(): ApplicationTracker {
    if (!this.lmGlobalTrackerService.getApplicationTracker()) {
      this.lmGlobalTrackerService.setApplicationtracker(this.createApplicationTracker());
      // start tracking for the application
      this.startApplicationTracker();
    }

    return this.lmGlobalTrackerService.getApplicationTracker();
  }

  private startApplicationTracker() {
    this.getApplicationTracker().trackStart();
  }

  private endApplicationTracker() {
    this.getApplicationTracker().trackEnd();
  }

  private startExternalViewTracker(name?: string, type?: string, attributes?: Attributes, tags?: (string)[]) {
    if (this.viewExternalTracker) {
      this.getExternalViewTracker().trackEnd();
      this.viewExternalTracker = undefined;
    }
    this.getExternalViewTracker(name, type, attributes, tags).trackStart();
  }

  private startViewTracker(attributes: Attributes, tags?: (string)[]) {
    this.getViewTracker(attributes, tags).trackStart();
  }

  private endViewTracker() {
    //  this.getViewTracker().attributes = attributes;
    this.getViewTracker().trackEnd();
    if (this.viewExternalTracker) {
      this.getExternalViewTracker().trackEnd();
    }
  }

  private getLogicalId() {
    return this.lmContextService.getContext().logicalId ? this.lmContextService.getContext().logicalId : 'landmark-ui';
  }

  private getSessionId() {
    return this.lmContextService.getContext().actorUniqueId;  // + '-' + this.getLogicalId();
  }

  private createApplicationTracker(): ApplicationTracker {
    if (!this.lmContextService.isTrackerEnabled()) {
      return;
    }
    if (!this.lmGlobalTrackerService.getApplicationTracker()) {
    const applicationTracker: ApplicationTracker = new ApplicationTracker({
        name: this.lmContextService.getUrlPdlOrDa(),
        identifier: this.getLogicalId(), // "lid://ui.landmark",
        trackingClient: this.getTrackingClient(),
      });
      this.lmGlobalTrackerService.setApplicationtracker(applicationTracker);
    }
    return this.lmGlobalTrackerService.getApplicationTracker();
  }

  private createTrackingClient(): TrackingClient {
    const trackerAccess = this.lmContextService.getTrackerAccess()
    const loginFn = async () => {
      return trackerAccess;
    };


    if (environment.production && trackerAccess?.accessToken) { //environment.production && !this.lmGlobalUtilService.isOverrideTrackerLogging()) {
      try {
        const stc: StandaloneTrackingClient = new StandaloneTrackingClient({
          baseUrl: this.lmContextService.getTrackerPublicUrl(),
          auth: {
            type: "custom",
            login: loginFn
          },
        });
        this.trackingClient = stc;
      } catch (e) {
        // if we have the trackng turned on and we are not able to connect to the tracking server and create a tracking client create it locally.
        this.trackingClient = new DevTrackingClient();
      }
    } else {
      this.trackingClient = new DevTrackingClient();
    }
    return  this.trackingClient;
  }

  // we should not have a loadTime less than 0. If we do log information to help figure out what is going on
  // log to server load times of > 5 minutes and -1 with addition debug information.
  private serverLogTrackerIssues(componentName: string, url: string, info: LMComponentInfo, loadTime: number): boolean {
    if (loadTime > 300000) {   // 5 minutes or longer.
      const jsonLog = {
        message: `TRACKERLIB DEBUG - $componentName}: Possible excessive loadtime > 5 minutes`,
        loadTime: loadTime,
        componentInfo: info,
        referrer: document.referrer,
        url: url
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
      return false;
    }

    if (loadTime > 0) {
      return true;
    }

    const jsonLog = {
      message: `TRACKERLIB DEBUG - $componentName}: Error value of -1 in loadtime`,
      loadTime: loadTime,
      componentInfo: info,
      referrer: document.referrer,
      url: url
    };

    console.error(jsonLog);
    this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    return false;
  }
}







